import type { AxiosInstance } from 'axios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { classifyLinkageResponse, type DocumentLinkageResult } from 'shared/documentLinkage';
import type {
  CreateDeliverableMetadataDto,
  DbAnalystDto,
  DeleteDeliverableResponseDto,
  DeliverableListItemDto,
  DeliverableUsageDto,
  DocumentLinkageDto,
  LovEffortLevelDto,
  LovServiceTypeDto,
  NotifyCandidateDto,
  NotifyDeliverableRequestDto,
  NotifyDeliverableResponseDto,
  PromoteDeliverableRequestDto,
  ResolveDeliverableResponseDto,
  UpdateDeliverableMetadataDto
} from './types';

const deliverablesPath = (dealId: string): string => `/deals/${encodeURIComponent(dealId.trim())}/deliverables`;

/** `lov_service_type` catalog — shared NPI service-type list (Decision 1). */
const SERVICE_TYPES_PATH = '/lov/service-type';

/** NPI analyst roster for the New-Deliverable "Analyst" select (Decision 1). */
const ANALYSTS_PATH = '/user/analysts';

/** `lov_effort_level` catalog — includes RETIRED (`isActive: false`) levels, ordered by `order_by` (E-2/E-4). */
const EFFORT_LEVELS_PATH = '/lov/effort-level';

/**
 * Builds the multipart body for `POST /deals/:dealId/deliverables/upload`.
 *
 * One uploaded file = one deliverable (Decision 2), so `deliverables_metadata` is a
 * JSON-stringified **single-element** array (one entry per file, parallel order) and
 * the file is appended under the `files` field — the names the controller declares
 * (`ConfigurableFilesInterceptor({ fieldName: 'files' })` + `UploadDeliverablesDto`).
 * Mirrors `appendConfirmedDocumentsToFormData` in `api/useNewDeal`. Pure + exported so
 * it can be unit-tested without rendering.
 * @param {File} file - The single file to upload.
 * @param {CreateDeliverableMetadataDto} meta - Service type code + title for this file.
 * @returns {FormData} Multipart body; `Content-Type` is left to the browser (see `useAxios`).
 */
export function buildDeliverableUploadFormData(file: File, meta: CreateDeliverableMetadataDto): FormData {
  const formData = new FormData();
  formData.append('deliverables_metadata', JSON.stringify([meta]));
  formData.append('files', file, file.name);
  return formData;
}

/**
 * Read API for the deal Deliverables panel. Mirrors `dealProposalApi.ts`:
 * each method resolves `undefined` on error (after `AxiosErrorHandler`); the
 * shared `useAxios` interceptor already surfaces a toast.
 */
export interface DeliverablesApiClient {
  /** List active deliverables for a deal → `GET /deals/:dealId/deliverables`. */
  getDeliverables(dealId: string): Promise<DeliverableListItemDto[] | undefined>;
  /** The service-type catalog that feeds the type filter chips → `GET /lov/service-type`. */
  getServiceTypes(): Promise<LovServiceTypeDto[] | undefined>;
  /** The bounded NPI analyst roster that feeds the required Analyst select → `GET /user/analysts`. */
  getAnalysts(): Promise<DbAnalystDto[] | undefined>;
  /**
   * The effort-level catalog that feeds the optional Effort Level select → `GET /lov/effort-level`.
   * Returns RETIRED levels too (`isActive: false`), in `order_by` sequence — the drawer renders them
   * disabled rather than filtering them out (E-2/E-4). Declared optional so an injected test/mock
   * client can omit it. Resolves `undefined` on error, which degrades to an empty (still optional) select.
   */
  getEffortLevels?(): Promise<LovEffortLevelDto[] | undefined>;
  /**
   * Uploads a single file as one new deliverable (Decision 2 — 1 file = 1 deliverable)
   * → `POST /deals/:dealId/deliverables/upload`, returning the deal's deliverables.
   * Declared optional so an injected test/mock client can omit it (the real
   * `createDeliverablesApi` always provides it). Resolves `undefined` on error.
   */
  uploadDeliverable?(
    dealId: string,
    file: File,
    meta: CreateDeliverableMetadataDto
  ): Promise<DeliverableListItemDto[] | undefined>;
  /**
   * Pre-flight for delete — fetches linked-deal count + sibling deal identifiers.
   * → `GET /deals/:dealId/deliverables/:deliverableId/usage`.
   * Resolves `undefined` on error (after `AxiosErrorHandler` toast); the panel
   * aborts the delete flow when undefined is returned (Decision 8).
   */
  getUsage?(dealId: string, deliverableId: string): Promise<DeliverableUsageDto | undefined>;
  /**
   * Hard-deletes the deliverable and its unshared documents; lays S3 delete-markers post-commit.
   * → `DELETE /deals/:dealId/deliverables/:deliverableId`.
   * Resolves `undefined` on error (after `AxiosErrorHandler` toast).
   */
  deleteDeliverable?(dealId: string, deliverableId: string): Promise<DeleteDeliverableResponseDto | undefined>;
  /**
   * Cross-link-type pre-flight for the destroy-everywhere delete warning, keyed by `document_id`
   * → `GET /deals/:dealId/documents/:documentId/linkage`. Reports whether the document is also a
   * deal document / on which live proposals / backs which deliverables, so the delete-confirm can
   * NAME what the destroy will also remove. Resolves `ok: false` WITH A REASON on error — the caller
   * treats that as fail-closed (does NOT open a destructive confirm and does NOT delete; see Stage 2
   * override) and reports `forbidden` as a permission problem instead of inviting a futile retry.
   */
  getDocumentLinkage?(dealId: string, documentId: string): Promise<DocumentLinkageResult<DocumentLinkageDto>>;
  /**
   * Read-only pre-flight the drawer calls on file-pick → `GET /deals/:dealId/deliverables/resolve?fileName=...`.
   * Reports whether the picked file collides with an existing `document` (making a normal upload 409) and this
   * deal's linkage, so the drawer can offer "promote" instead. Resolves `undefined` on error — the drawer then
   * degrades gracefully to the normal upload path (never bricks on a failed/slow resolve).
   */
  resolveDeliverable?(dealId: string, fileName: string): Promise<ResolveDeliverableResponseDto | undefined>;
  /**
   * Promotes an existing `document` into a new deliverable (reuses the row — no re-upload, no `document.create`,
   * so no `storage_key` 409) → `POST /deals/:dealId/deliverables/promote`. Returns the single promoted deliverable.
   * Resolves `undefined` on error (after `AxiosErrorHandler` toast).
   */
  promoteDeliverable?(dealId: string, body: PromoteDeliverableRequestDto): Promise<DeliverableListItemDto | undefined>;
  /**
   * Client-user lookup behind the drawer's "Add someone else" combobox (spec §4b, D-6)
   * → `GET /deals/:dealId/deliverables/notify-candidates?q=…`. Returns a **bare array**, capped
   * server-side at 25 post-match results with **no truncation signal** in the payload.
   *
   * An empty/blank `q` returns `[]` **by design** — the endpoint never dumps the client's roster —
   * and every call decrypts the whole client roster, so callers must debounce the input rather than
   * calling per keystroke. Declared optional so an injected test/mock client can omit it.
   * Resolves `undefined` on error (after `AxiosErrorHandler` toast); the lookup then shows no results.
   */
  getNotifyCandidates?(dealId: string, q: string): Promise<NotifyCandidateDto[] | undefined>;
  /**
   * Emails the selected client recipients that a deliverable was published
   * → `POST /deals/:dealId/deliverables/:deliverableId/notify`. Fired by the drawer **after** a
   * successful upload (locked decision 1), never as part of it.
   *
   * The body is snake_case (`recipient_user_ids`) while the response is camelCase, and an **empty**
   * array is a 400 (`@ArrayNotEmpty()`) — callers must skip the call when nothing is selected.
   * The endpoint answers **201 even when every send failed**, so the caller reads the outcome from
   * `failedUserIds` / `skippedUserIds`, never from the status.
   *
   * Resolves `undefined` on error (after `AxiosErrorHandler` toast), which — per the house
   * convention — collapses a rejected recipient (404/400, nobody emailed) and a transport failure
   * into one indistinguishable value; the drawer reports both with a single honest message.
   */
  notifyDeliverable?(
    dealId: string,
    deliverableId: string,
    body: NotifyDeliverableRequestDto
  ): Promise<NotifyDeliverableResponseDto | undefined>;
  /**
   * Partially updates a deliverable's metadata → `PATCH /deals/:dealId/deliverables/:deliverableId`.
   * Send only changed keys (an empty body is a 400); `effort_level_id: null` clears. Returns the
   * GET-shaped row. Optional so a mock client can omit it; resolves `undefined` on error.
   */
  updateDeliverable?(
    dealId: string,
    deliverableId: string,
    body: UpdateDeliverableMetadataDto
  ): Promise<DeliverableListItemDto | undefined>;
}

/**
 * Creates deliverables API functions bound to an authenticated axios instance
 * (e.g. from `useAxios(BASE_URL_V2)`).
 * @param {AxiosInstance} axios - Axios instance with base URL and auth interceptors.
 * @returns {DeliverablesApiClient} API methods; resolve `undefined` on error after `AxiosErrorHandler`.
 */
export function createDeliverablesApi(axios: AxiosInstance): DeliverablesApiClient {
  return {
    async getDeliverables(dealId: string): Promise<DeliverableListItemDto[] | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const res = await axios.get<DeliverableListItemDto[]>(deliverablesPath(id));
        return res.data;
      } catch (e) {
        console.error('getDeliverables:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async getServiceTypes(): Promise<LovServiceTypeDto[] | undefined> {
      try {
        const res = await axios.get<LovServiceTypeDto[]>(SERVICE_TYPES_PATH);
        return res.data;
      } catch (e) {
        console.error('getServiceTypes:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async getAnalysts(): Promise<DbAnalystDto[] | undefined> {
      try {
        const res = await axios.get<DbAnalystDto[]>(ANALYSTS_PATH);
        return res.data;
      } catch (e) {
        console.error('getAnalysts:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async getEffortLevels(): Promise<LovEffortLevelDto[] | undefined> {
      try {
        const res = await axios.get<LovEffortLevelDto[]>(EFFORT_LEVELS_PATH);
        return res.data;
      } catch (e) {
        console.error('getEffortLevels:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async uploadDeliverable(
      dealId: string,
      file: File,
      meta: CreateDeliverableMetadataDto
    ): Promise<DeliverableListItemDto[] | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const formData = buildDeliverableUploadFormData(file, meta);
        const res = await axios.post<DeliverableListItemDto[]>(`${deliverablesPath(id)}/upload`, formData);
        return res.data;
      } catch (e) {
        console.error('uploadDeliverable:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async getUsage(dealId: string, deliverableId: string): Promise<DeliverableUsageDto | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const res = await axios.get<DeliverableUsageDto>(
          `${deliverablesPath(id)}/${encodeURIComponent(deliverableId)}/usage`
        );
        return res.data;
      } catch (e) {
        console.error('getUsage:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async deleteDeliverable(dealId: string, deliverableId: string): Promise<DeleteDeliverableResponseDto | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const res = await axios.delete<DeleteDeliverableResponseDto>(
          `${deliverablesPath(id)}/${encodeURIComponent(deliverableId)}`
        );
        return res.data;
      } catch (e) {
        console.error('deleteDeliverable:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async getDocumentLinkage(dealId: string, documentId: string): Promise<DocumentLinkageResult<DocumentLinkageDto>> {
      try {
        const id = dealId.trim();
        const docId = documentId.trim();
        if (!id || !docId) return { ok: false, reason: 'unavailable' };
        // Document-centric endpoint — NOT under the deliverables path.
        // Typed `unknown`: useAxios's interceptor RESOLVES with the AxiosError on an HTTP failure
        // (see documentLinkage.ts), so this is not necessarily a response. classify* narrows it.
        const res: unknown = await axios.get<DocumentLinkageDto>(
          `/deals/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/linkage`
        );
        return classifyLinkageResponse<DocumentLinkageDto>(res);
      } catch (e) {
        console.error('getDocumentLinkage:', e);
        // Telemetry WITHOUT rethrowing — see the same note in useNewDeal.getDocumentLinkage.
        void AxiosErrorHandler(e).catch(() => undefined);
        return { ok: false, reason: 'unavailable' };
      }
    },

    async resolveDeliverable(dealId: string, fileName: string): Promise<ResolveDeliverableResponseDto | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        // `fileName` is a query param, not a path segment — let axios encode it via `params`.
        const res = await axios.get<ResolveDeliverableResponseDto>(`${deliverablesPath(id)}/resolve`, {
          params: { fileName }
        });
        return res.data;
      } catch (e) {
        console.error('resolveDeliverable:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async promoteDeliverable(
      dealId: string,
      body: PromoteDeliverableRequestDto
    ): Promise<DeliverableListItemDto | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const res = await axios.post<DeliverableListItemDto>(`${deliverablesPath(id)}/promote`, body);
        return res.data;
      } catch (e) {
        console.error('promoteDeliverable:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async getNotifyCandidates(dealId: string, q: string): Promise<NotifyCandidateDto[] | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        // `q` is a query param, not a path segment — let axios encode it via `params`.
        const res = await axios.get<NotifyCandidateDto[]>(`${deliverablesPath(id)}/notify-candidates`, {
          params: { q }
        });
        return res.data;
      } catch (e) {
        console.error('getNotifyCandidates:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async notifyDeliverable(
      dealId: string,
      deliverableId: string,
      body: NotifyDeliverableRequestDto
    ): Promise<NotifyDeliverableResponseDto | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const res = await axios.post<NotifyDeliverableResponseDto>(
          `${deliverablesPath(id)}/${encodeURIComponent(deliverableId)}/notify`,
          body
        );
        return res.data;
      } catch (e) {
        console.error('notifyDeliverable:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    },

    async updateDeliverable(
      dealId: string,
      deliverableId: string,
      body: UpdateDeliverableMetadataDto
    ): Promise<DeliverableListItemDto | undefined> {
      try {
        const id = dealId.trim();
        if (!id) return undefined;
        const res = await axios.patch<DeliverableListItemDto>(
          `${deliverablesPath(id)}/${encodeURIComponent(deliverableId)}`,
          body
        );
        return res.data;
      } catch (e) {
        console.error('updateDeliverable:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    }
  };
}
